import dynamic from "next/dynamic";
import Fixed from "../layouts/Fixed";
import type { GetServerSidePropsResult, NextPage } from "next";
import Head from "next/head";
import styles from "../styles/Home.module.css";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import {
  ContentfulHelpIcons,
  HelpIconInterface,
  Microcopy,
  MicrocopyResource,
  Partner,
} from "../utilities/contentful";
import { useContext, useEffect, useState } from "react";
import Maybe from "../components/common/Maybe";
import { SavedQuotes } from "../utilities/saved-quotes";
import { QuoteRequest } from "../components/quote/QuoteRequestData";
import {
  FullQuoteResult,
  QuoteData,
  QuoteResult,
} from "../components/quote/QuoteData.interface";
import AppContext from "../AppContext";
import { useRouter } from "next/router";
import { submitQuoteForm } from "../components/quote/Form/QuoteHelpers";
import { Titles, Meta, config, Images, HeroContent, Landing } from "@brand";
import HomeHero from "components/home/HomeHero";
import CoverageOptions from "components/home/CoverageOptions";
import { PAGE_GUTTER } from "../utilities/layout";
import {
  forgetTrackingParams,
  AFFILIATE_COOKIE,
  AGENT_COOKIE,
  readTrackingCookie,
} from "../utilities/tracking";
import {
  affiliateCodeFor,
  partnerQueryParams,
  producerNumberFor,
} from "../utilities/partners";
import ArrowRightAltSharpIcon from "@mui/icons-material/ArrowRightAltSharp";
import { Topbar } from "layouts/Fixed/components";
import ProgressLoader from "components/common/ProgressLoader";

const NeedHelp = dynamic(() => import("../components/quote/Layout/NeedHelp"));
const blockedByAgency = (results: QuoteResult[]): boolean =>
  results.length > 0 &&
  results.every((result) =>
    (result.quote?.brokenRules ?? []).some((rule) =>
      /agent restrictions|list of agents/i.test(
        (rule as {name?: string})?.name ?? ''
      )
    )
  );

const Productbox = dynamic(
  () => import("../components/quote/Layout/ProductBox")
);
const ComparePlansModal = dynamic(
  () => import("../components/quote/Layout/ComparePlansModal")
);
const ProductDetailsModal = dynamic(
  () => import("../components/quote/Layout/ProductDetailsModal")
);

interface HomeProps {
  microcopySet: MicrocopyResource[];
  restoredQuote: QuoteRequest | null;
  quoteResult: FullQuoteResult | null;
  restoredQuoteError: boolean;
  helpIcons: HelpIconInterface[];
  brand: ContentfulAgent;
  partner: ContentfulPartner | null;
  agentNum: string;
  affiliate: string;
}

const Home: NextPage<HomeProps> = (props: HomeProps) => {
  const [selectedProduct, setSelectedProduct] = useState({
    details: null,
    quoteData: null,
    premium: null,
  } as { details: ContentfulProduct | null; quoteData: QuoteData | null; premium: string | null });

  const [quoteData, setQuoteData] = useState(props.quoteResult);
  const [errorModalOpen, setErrorModalOpen] = useState(
    props.restoredQuoteError
  );
  const [productsForComparison, setProductsForComparison] = useState<
    ContentfulProduct[]
  >([]);
  const appState: any = useContext(AppContext);

  const router = useRouter();

  useEffect(() => {
    if (props.restoredQuote || props.restoredQuoteError) {
      const queryString = {
        ...(router.query as { quote?: string; agent?: string; affiliate?: string }),
        ...partnerQueryParams(props.partner),
      };
      delete queryString.quote;

      router.replace({
        query: { ...queryString },
      });
    }

    // remove confirmation data from local storage
    if (
      typeof window !== "undefined" &&
      localStorage.getItem("selected-product")
    ) {
      localStorage.removeItem("post-purchase-quote-form");
      localStorage.removeItem("post-purchase-address-form");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const quoteResult = props.quoteResult || quoteData;
    const products = (quoteResult?.data || [])
      .map((quote) => quote.product)
      .filter(Boolean);
    setProductsForComparison(products);
  }, [props.quoteResult || quoteData]);

  const handleOpenQuote = () => {
    appState.setAppState({
      ...appState.state,
      quoteModalOpen: true,
    });
  };

  const leavePartner = () => {
    forgetTrackingParams();
    window.location.assign("/");
  };

  const handleSetSelectedProduct = (quoteResult: QuoteResult) => {
    setSelectedProduct({
      details: quoteResult.product,
      quoteData: quoteResult.quote,
      premium: quoteResult.premium,
    });

    appState.setAppState({
      ...appState.state,
      detailsModalOpen: true,
    });
  };

  return (
    <Fixed
      helpIcons={props.helpIcons}
      quoteMicrocopy={props.microcopySet}
      restoredQuote={props.restoredQuote}
      setQuoteData={setQuoteData}
      brand={props.brand}
      partner={props.partner}
    >
      <Box>
        <Head>
          <title>{Titles.index}</title>
          <meta name={Meta.index.name} content={Meta.index.content} />
          <link rel="icon" href="/favicon.ico" />
        </Head>

        <main className={styles.main}>
          <Container
            maxWidth={false}
            disableGutters={true}
            sx={{ paddingX: PAGE_GUTTER }}
          >
            <Box sx={{ marginY: "28px" }}>
              <Topbar
                helpIcons={props.helpIcons}
                quoteMicrocopy={props.microcopySet}
                restoredQuote={props.restoredQuote}
                setQuoteData={setQuoteData}
                agentNum={props.agentNum}
                affiliate={props.affiliate}
              />
            </Box>

            <Maybe test={!quoteData && !appState.state.loadingQuoteResult}>
              <Box
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
              >
                <Maybe test={appState.state.quoteRequestError}>
                  <Box
                    display={"flex"}
                    justifyContent={"center"}
                    sx={{ marginBottom: 4 }}
                  >
                    <Typography variant={"h6"}>
                      There was an unexpected error retrieving your quote.
                      Please try again.
                    </Typography>
                  </Box>
                </Maybe>
                <Maybe test={!appState.state.quoteRequestError}>
                  <HomeHero
                    backgroundImage={Images.hero_bcg}
                    backgroundSize={Images.hero_bcg_size}
                    backgroundPosition={Images.hero_bcg_position}
                    content={<HeroContent />}
                    actions={
                    <Box>
                      <Button
                        variant="contained"
                        onClick={handleOpenQuote}
                        endIcon={<ArrowRightAltSharpIcon />}
                        sx={{ borderRadius: "8px", textTransform: "none" }}
                      >
                        {Landing.heroCta}
                      </Button>
                    </Box>
                    }
                  ></HomeHero>
                  <CoverageOptions onSelect={handleOpenQuote} />
                </Maybe>
                <Box sx={{ marginTop: "24px", width: "100%" }}>
                  <NeedHelp agent={props.brand} />
                </Box>
              </Box>
            </Maybe>

            <Maybe test={appState.state.loadingQuoteResult}>
              <Box
                sx={{
                  borderRadius: "20px",
                  display: "flex",
                  justifyContent: "center",
                  paddingBlock: "84px",
                  backgroundColor: "white",
                }}
              >
                <Box
                  sx={{
                    flexDirection: "column",
                    display: "flex",
                    alignItems: "center",
                    width: {
                      sm: "76.5%",
                      lg: "920px",
                    },
                    gap: {
                      sm: "40px",
                      lg: "109px",
                    },
                  }}
                >
                  <Typography variant={"h2"} fontSize={"36px"} fontWeight={400}>
                    Loading quote result
                  </Typography>
                  <ProgressLoader dotSize={13} />
                  <Typography
                    variant={"body1"}
                    sx={{
                      fontSize: "14px",
                      textAlign: "center",
                      width: {
                        lg: "704px",
                      },
                    }}
                  >
                    Benefits are subject to exclusions and limitations and are
                    payable only at Usual, Reasonable, and Customary charges.
                    This is a summary of a selection of plan benefits offered
                    only as an illustration and does not supersede in any way
                    the Certificate of Insurance or the Application, Master
                    Policy and any riders or other governing documents (the
                    “Insurance Contract”). The Insurance Contract is the only
                    source of the actual benefits provided.
                  </Typography>
                </Box>
              </Box>
            </Maybe>

            <Maybe
              test={
                (props.quoteResult?.data?.length || quoteData?.data?.length) &&
                !appState.state.loadingQuoteResult
              }
            >
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "18px" }}
              >
                <Maybe
                  test={
                    props.partner &&
                    blockedByAgency(
                      props.quoteResult?.data || quoteData?.data || []
                    )
                  }
                >
                  <Alert
                    severity="info"
                    action={
                      <Button color="inherit" size="small" onClick={leavePartner}>
                        See standard pricing
                      </Button>
                    }
                  >
                    {props.partner?.name} cannot offer these plans, so no prices
                    are shown. You can continue without the agency.
                  </Alert>
                </Maybe>
                {/* A plan whose Contentful entry did not come back has nothing
                    to show on its card; skip it rather than lose the page. */}
                {(props.quoteResult?.data || quoteData?.data || [])
                  .filter((quoteObj) => !!quoteObj.product)
                  .map(
                  (quoteObj) => {
                    return (
                      <Productbox
                        key={`${quoteObj.quote.appType}-${quoteObj.quote.productCode}`}
                        quoteData={quoteObj.quote}
                        totalAmount={quoteObj.premium}
                        product={quoteObj.product}
                        duration={quoteObj.duration}
                        handleSetSelectedProduct={() =>
                          handleSetSelectedProduct(quoteObj)
                        }
                        detailView={false}
                        microcopySet={props.microcopySet}
                        agentNum={props.agentNum}
                        partner={props.partner}
                      />
                    );
                  }
                )}
              </Box>
              <Maybe
                test={
                  !props.quoteResult?.data?.length && !quoteData?.data?.length
                }
              >
                <Box
                  display={"flex"}
                  justifyContent={"center"}
                  sx={{ marginBottom: 4 }}
                >
                  <Typography variant={"h5"}>
                    We weren't able to find any plans matching your query...
                  </Typography>
                </Box>
              </Maybe>

              <ComparePlansModal products={productsForComparison} />

              <ProductDetailsModal
                details={selectedProduct.details}
                quoteData={selectedProduct.quoteData}
                totalAmount={selectedProduct.premium || ""}
                duration={selectedProduct.quoteData?.duration as number}
                microcopySet={props.microcopySet}
                agentNum={props.agentNum}
                partner={props.partner}
              />
            </Maybe>

            {/* <pre style={{ textAlign: "left" }}>
              {JSON.stringify(appState.state, null, 2)}
            </pre> */}
          </Container>
        </main>
      </Box>

      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        scroll={"body"}
      >
        <DialogTitle>Retrieve a quote or resume your application</DialogTitle>
        <DialogContent>Failed to load save quote.</DialogContent>
      </Dialog>
    </Fixed>
  );
};

export async function getServerSideProps({
  query,
  req,
}: {
  query: { quote?: string; agent?: string; affiliate?: string };
  req: { headers: { cookie?: string } };
}): Promise<GetServerSidePropsResult<HomeProps>> {
  const partners = new Partner();
  const micro = new Microcopy();
  const savedQuotes = new SavedQuotes();
  const helpIcons = new ContentfulHelpIcons();
  const cookieHeader = req?.headers?.cookie;
  const affiliateParam =
    query.affiliate || readTrackingCookie(cookieHeader, AFFILIATE_COOKIE) || "";
  const partnerKey =
    query.agent ||
    query.affiliate ||
    readTrackingCookie(cookieHeader, AGENT_COOKIE) ||
    affiliateParam ||
    config.defaultAgentNumber;

  try {
    await micro.fetch("1ix9XrQxjHb1ipTJdFuyFC");
  } catch (e) {
    console.log("ERROR FETCHING MICROCOPY", e);
  }
  const getMicro = await micro.fetch("1ix9XrQxjHb1ipTJdFuyFC");
  const getHelpIcons = await helpIcons.fetch();

  let savedQuote = null;
  let quoteResult = null;
  let restoredAgentNum = null;
  let restoredAffiliateCode = "";

  if (query.quote) {
    const getQuote = await savedQuotes.getQuote(query.quote);
    savedQuote = getQuote?.quoteData as QuoteRequest;
    restoredAgentNum = getQuote?.agentNum as string;
    restoredAffiliateCode = getQuote?.affiliateCode ?? "";

    if (savedQuote) {
      quoteResult = savedQuote
        ? await submitQuoteForm(
            { ...savedQuote, saveQuote: false },
            restoredAgentNum
          )
        : null;
    }
  }

  const restoredPartnerKey = restoredAffiliateCode || restoredAgentNum;

  const { brand, partner } = await partners.resolve(
    restoredPartnerKey || partnerKey
  );

  return {
    props: {
      microcopySet: getMicro.microcopySet.resourcesCollection.items,
      helpIcons: getHelpIcons.helpIcons.resourcesCollection.items,
      restoredQuote: savedQuote ? savedQuote : null,
      quoteResult: quoteResult,
      restoredQuoteError: !!(query.quote && savedQuote === undefined),
      brand: brand,
      partner: partner,
      agentNum: producerNumberFor(partner),
      affiliate: affiliateCodeFor(
        partner,
        restoredPartnerKey ? restoredAffiliateCode : affiliateParam
      ),
    },
  };
}

export default Home;
